export const API_BASE_URL = process.env.REACT_APP_API_URL;

// Simple cache to avoid repeated API calls
const cache = new Map();
const tableListeners = new Map();

const notifyTableChange = (resource) => {
  tableListeners.get(resource)?.forEach(listener => listener());
};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCacheKey = (url, options) => {
  return `${options.method || 'GET'}_${url}`;
};

const isCacheValid = (timestamp) => {
  return Date.now() - timestamp < CACHE_DURATION;
};

const apiRequest = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    ...options.headers,
  };
  
  // Don't set Content-Type for FormData (let browser set it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Check cache for GET requests only (skip if noCache flag set)
  const cacheKey = getCacheKey(url, options);
  if (!options.method || options.method === 'GET') {
    if (!options.noCache) {
      const cached = cache.get(cacheKey);
      if (cached && isCacheValid(cached.timestamp)) {
        return cached.data;
      }
    } else {
      cache.delete(cacheKey);
    }
  }
  
  const { noCache, ...fetchOptions } = options;
  const response = await fetch(`${API_BASE_URL}${url}`, {
    headers,
    ...fetchOptions,
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.reload();
      return;
    }
    
    let errorMessage = 'Request failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }
    
    const error = new Error(errorMessage);
    error.status = response.status;
    throw error;
  }
  
  const data = await response.json();
  
  // Cache GET requests
  if (!options.method || options.method === 'GET') {
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  } else {
    // Clear related cache on mutations
    const urlParts = url.split('/');
    const resource = urlParts[1]; // e.g., 'workers', 'attendance'
    for (const key of cache.keys()) {
      if (key.includes(resource)) {
        cache.delete(key);
      }
    }
    notifyTableChange(resource);
  }
  
  return data;
};

export const tableRequest = async (url, params = {}, options = {}) => {
  const cleanParams = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''));
  const query = new URLSearchParams(cleanParams).toString();
  return apiRequest(query ? `${url}?${query}` : url, { noCache: true, ...options });
};

export const TABLE_CONFIGS = {
  users: { endpoint: '/users', defaultParams: { sortBy: 'createdAt', sortOrder: 'desc' } },
  workers: { endpoint: '/workers', defaultParams: { sortBy: 'id', sortOrder: 'desc' } },
  attendance: { endpoint: '/attendance', defaultParams: { sortBy: 'date', sortOrder: 'desc' } },
  leaves: { endpoint: '/leaves', defaultParams: { sortBy: 'id', sortOrder: 'desc' } },
  leaveTypes: { endpoint: '/leave-types', defaultParams: { sortBy: 'id', sortOrder: 'desc' } },
  holidays: { endpoint: '/holidays', defaultParams: { sortBy: 'fromDate', sortOrder: 'desc' } },
  shifts: { endpoint: '/shifts', defaultParams: { sortBy: 'id', sortOrder: 'desc' } },
  payroll: { endpoint: '/payroll', defaultParams: { sortBy: 'startDate', sortOrder: 'desc' } },
  paymentTerms: { endpoint: '/payment-terms', defaultParams: { sortBy: 'created_at', sortOrder: 'desc' } }
};
export const createTableSource = (source, overrides = {}) => {
  const endpoint = typeof source === 'string' ? source : source.endpoint;
  const defaultParams = { ...(typeof source === 'string' ? {} : source.defaultParams), ...overrides };
  const resource = endpoint.split('/').filter(Boolean)[0];
  return {
    endpoint,
    get: (params = {}, options = {}) => tableRequest(endpoint, { paginated: true, ...defaultParams, ...params }, options),
    subscribe: (listener) => {
      if (!tableListeners.has(resource)) tableListeners.set(resource, new Set());
      tableListeners.get(resource).add(listener);
      return () => tableListeners.get(resource)?.delete(listener);
    }
  };
};
const api = {
  dashboard: {
    getSummary: () => apiRequest('/dashboard/summary', { noCache: true })
  },
  auth: {
    login: (data) => apiRequest('/auth/login', { method: 'POST', body: JSON.stringify(data) }),
    register: (data) => apiRequest('/auth/register', { method: 'POST', body: JSON.stringify(data) })
  },
  users: {
    table: createTableSource(TABLE_CONFIGS.users),
    getAll: () => apiRequest('/users'),
    create: (data) => apiRequest('/users', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/users/${id}`, { method: 'DELETE' }),
    assignRole: (userId, data) => apiRequest(`/users/${userId}/role`, { method: 'PUT', body: JSON.stringify(data) })
  },
  workers: {
    getAll: () => apiRequest('/workers'),
    table: createTableSource(TABLE_CONFIGS.workers),
    getTable: (params = {}) => tableRequest('/workers', { paginated: true, ...params }),
    checkDuplicate: (data) => apiRequest('/workers/duplicate-check', { method: 'POST', body: JSON.stringify(data) }),
    create: (data) => apiRequest('/workers', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/workers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/workers/${id}`, { method: 'DELETE' }),
    getById: (id) => apiRequest(`/workers/${id}`),
    getNextUnid: () => apiRequest('/workers/next-unid')
  },
  attendance: {
    table: createTableSource(TABLE_CONFIGS.attendance),
    getAll: () => apiRequest('/attendance'),
    create: (data) => apiRequest('/attendance', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/attendance/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/attendance/${id}`, { method: 'DELETE' })
  },
  leaves: {
    table: createTableSource(TABLE_CONFIGS.leaves),
    getAll: () => apiRequest('/leaves', { noCache: true }),
    create: (data) => apiRequest('/leaves', { method: 'POST', body: JSON.stringify(data) }),
    updateStatus: (id, status) => apiRequest(`/leaves/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) })
  },
  leaveTypes: {
    table: createTableSource(TABLE_CONFIGS.leaveTypes),
    getAll: () => apiRequest('/leave-types'),
    create: (data) => apiRequest('/leave-types', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/leave-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/leave-types/${id}`, { method: 'DELETE' })
  },
  holidays: {
    table: createTableSource(TABLE_CONFIGS.holidays),
    getAll: () => apiRequest('/holidays'),
    create: (data) => apiRequest('/holidays', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/holidays/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/holidays/${id}`, { method: 'DELETE' })
  },
  shifts: {
    table: createTableSource(TABLE_CONFIGS.shifts),
    getAll: () => apiRequest('/shifts'),
    create: (data) => apiRequest('/shifts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/shifts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/shifts/${id}`, { method: 'DELETE' })
  },
  payroll: {
    table: createTableSource(TABLE_CONFIGS.payroll),
    getAll: () => apiRequest('/payroll'),
    create: (data) => apiRequest('/payroll', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/payroll/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/payroll/${id}`, { method: 'DELETE' }),
    getWorkerAttendance: (worker_id, startDate, endDate) => {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const queryString = params.toString();
      return apiRequest(`/payroll/worker-attendance/${worker_id}${queryString ? '?' + queryString : ''}`);
    }
  },
  processStages: {
    getAll: () => apiRequest('/process-stages', { noCache: true }),
    create: (data) => apiRequest('/process-stages', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/process-stages/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/process-stages/${id}`, { method: 'DELETE' })
  },
  countries: {
    getAll: () => apiRequest('/countries')
  },
  states: {
    getAll: () => apiRequest('/states')
  },
  cities: {
    getAll: () => apiRequest('/cities')
  },
  companyIdTypes: {
    getAll: () => apiRequest('/company-id-types')
  },
  jewelleryGroups: {
    getAll: () => apiRequest('/jewellery-groups')
  },
  stoneTypes: {
    getAll: () => apiRequest('/stone-types', { noCache: true })
  },
  currencies: {
    getAll: () => apiRequest('/currencies', { noCache: true })
  },
  diamondTypes: {
    getAll: () => apiRequest('/diamond-types', { noCache: true })
  },
  userPermissions: {
    getByUserId: (userId) => apiRequest(`/user-permissions/user/${userId}`),
    create: (data) => apiRequest('/user-permissions', { method: 'POST', body: JSON.stringify(data) }),
    update: (userId, data) => apiRequest(`/user-permissions/${userId}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (userId) => apiRequest(`/user-permissions/${userId}`, { method: 'DELETE' })
  },
  roles: {
    getAll: () => apiRequest('/roles'),
    create: (data) => apiRequest('/roles', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/roles/${id}`, { method: 'DELETE' }),
    getPermissions: (roleId) => apiRequest(`/roles/${roleId}/permissions`),
    updatePermissions: (roleId, data) => apiRequest(`/roles/${roleId}/permissions`, { method: 'PUT', body: JSON.stringify(data) })
  },
  upload: {
    single: (formData) => apiRequest('/upload/single', { method: 'POST', body: formData }),
    multiple: (formData) => apiRequest('/upload/multiple', { method: 'POST', body: formData }),
    delete: (filename) => apiRequest(`/upload/file/${filename}`, { method: 'DELETE' }),
    getFile: (filename) => `${API_BASE_URL}/upload/file/${filename}`
  },
  paymentTerms: {
    table: createTableSource(TABLE_CONFIGS.paymentTerms),
    getAll: () => apiRequest('/payment-terms'),
    create: (data) => apiRequest('/payment-terms', { method: 'POST', body: JSON.stringify(data) }),
    update: (id, data) => apiRequest(`/payment-terms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id) => apiRequest(`/payment-terms/${id}`, { method: 'DELETE' })
  }

};

export default api;

